import AsyncStorage from '@react-native-async-storage/async-storage';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Uniwind } from 'uniwind';

export type Appearance = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'rbf.appearance';

type AppearanceContext = {
  appearance: Appearance;
  setAppearance: (value: Appearance) => void;
};

const Ctx = createContext<AppearanceContext>({
  appearance: 'light',
  setAppearance: () => {},
});

/**
 * Persists the user's Appearance preference and drives the Uniwind theme.
 * Defaults to "light" — the Field Manual design is light-first. A synchronous
 * `Uniwind.setTheme('light')` runs at module load (see app/_layout) to avoid a
 * first-frame flash; this provider then reconciles to the stored choice.
 */
export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [appearance, setState] = useState<Appearance>('light');

  useEffect(() => {
    (async () => {
      const stored = (await AsyncStorage.getItem(STORAGE_KEY)) as Appearance | null;
      if (stored) {
        setState(stored);
        Uniwind.setTheme(stored);
      }
    })();
  }, []);

  const setAppearance = (value: Appearance) => {
    setState(value);
    Uniwind.setTheme(value);
    void AsyncStorage.setItem(STORAGE_KEY, value);
  };

  return <Ctx.Provider value={{ appearance, setAppearance }}>{children}</Ctx.Provider>;
}

export const useAppearance = () => useContext(Ctx);
